import { useMemo, useState } from 'react'
import {
  useGetHazardIdentification,
  useUpdateHazardIdentification,
  useDeleteHazardIdentification,
  useApproveHazardIdentification,
  useMachineLocations,
} from '@/shared/hooks/api/machineryDirectiveCompliance'
import { useTranslation } from 'react-i18next'
import { Link, useLocation, useParams, useNavigate } from 'react-router'
import { resolveHazardDetailBack, hasCustomHazardDetailBack } from './hazardDetailNavigation'
import {
  isHazardUncertainReview,
  resolveKeepUncertainErrorMessage,
} from './hazardUncertainReview'
import { toast } from 'sonner'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { AddHazardDialog } from './AddHazardDialog'
import { DeleteHazardDialog } from './DeleteHazardDialog'
import { HazardCard } from './HazardCard'
import { HazardRiskAssessmentFlow } from './HazardRiskAssessmentFlow'
import { ComplianceSubPageHeader } from '../../components/ComplianceSubPageHeader'
import { ComplianceSubPageTitle } from '../../components/ComplianceSubPageTitle'

function getHazardTypeLabel(
  type: string | null,
  naLabel: string,
  translate: (key: string) => string
) {
  if (!type) {
    return naLabel
  }
  return translate(`hazardsPage.enums.hazardTypes.${type}`) || type
}

export function HazardDetailPage() {
  const params = useParams<{
    machineryDirectiveComplianceId: string
    hazardId: string
  }>()
  const complianceId = params.machineryDirectiveComplianceId
  const hazardId = params.hazardId
  const navigate = useNavigate()
  const location = useLocation()
  const hazardDetailBack = resolveHazardDetailBack(location.state)

  const [isFormOpen, setIsFormOpen] = useState(false)
  const [deletingHazardId, setDeletingHazardId] = useState<string | null>(null)

  const {
    data: hazard,
    isLoading,
    error,
  } = useGetHazardIdentification(complianceId, hazardId)
  const updateMutation = useUpdateHazardIdentification(complianceId ?? '')
  const deleteMutation = useDeleteHazardIdentification(complianceId ?? '')
  const approveMutation = useApproveHazardIdentification(complianceId ?? '')
  const { data: machineLocations = [] } = useMachineLocations(complianceId ?? '')
  const machineLocationsById = useMemo(
    () => new Map(machineLocations.map((location) => [location.id, location])),
    [machineLocations]
  )

  const { t } = useTranslation('machineryDirectiveCompliance')
  const naLabel = t('hazardsPage.hazardDetail.na')

  const handleEditClick = () => {
    setIsFormOpen(true)
  }

  const handleDeleteClick = () => {
    if (hazardId) {
      setDeletingHazardId(hazardId)
    }
  }

  const handleKeepClick = async () => {
    if (hazardId == null) {
      return
    }
    try {
      await approveMutation.mutateAsync(hazardId)
      toast.success(t('hazardsPage.keepUncertainSuccess'))
    } catch (err: unknown) {
      toast.error(resolveKeepUncertainErrorMessage(err, t))
    }
  }

  const isRemovingUncertain = isHazardUncertainReview(hazard?.uncertain)

  const handleConfirmDelete = async () => {
    if (!deletingHazardId || !complianceId) {
      return
    }
    try {
      await deleteMutation.mutateAsync(deletingHazardId)
      toast.success(
        isRemovingUncertain
          ? t('hazardsPage.removeUncertainSuccess')
          : t('hazardsPage.hazardDeletedSuccess')
      )
      setDeletingHazardId(null)
      navigate('..', { relative: 'path' })
    } catch (err: unknown) {
      const message =
        err instanceof Error
          ? err.message
          : isRemovingUncertain
            ? t('hazardsPage.removeUncertainFailed')
            : t('hazardsPage.deleteHazardFailed')
      toast.error(message)
    }
  }

  const handleFormUpdate = (
    payload: {
      hazardId: string
    } & import('@/shared/hooks/api/machineryDirectiveCompliance').UpdateHazardIdentificationPayload
  ) => {
    updateMutation.mutate(payload, {
      onSuccess: () => {
        setIsFormOpen(false)
      },
      onError: (err) => {
        toast.error(err.message ?? t('hazardsPage.addHazardFailed'))
      },
    })
  }

  if (complianceId == null || hazardId == null) {
    return null
  }

  if (isLoading) {
    return (
      <div className='flex items-center justify-center py-8'>
        <Skeleton className='h-10 w-48' />
        <Skeleton className='h-64 w-full max-w-xl' />
      </div>
    )
  }

  if (error || !hazard) {
    return (
      <div className='space-y-2 p-4'>
        <p className='text-destructive'>
          {error?.message ?? t('hazardsPage.hazardDetail.hazardNotFound')}
        </p>
        <Link
          to={hazardDetailBack.to}
          relative={hazardDetailBack.relative}
          className='text-sm text-primary hover:underline'
        >
          {hasCustomHazardDetailBack(location.state)
            ? t('hazardsPage.hazardDetail.back')
            : t('hazardsPage.hazardDetail.backToHazards')}
        </Link>
      </div>
    )
  }

  const isMutationPending =
    updateMutation.isPending ||
    deleteMutation.isPending ||
    approveMutation.isPending
  const needsReview = isHazardUncertainReview(hazard.uncertain)

  return (
    <>
      <div className='sticky top-0 z-10 border-b bg-background/95 py-3 backdrop-blur supports-[backdrop-filter]:bg-background/60'>
        <ComplianceSubPageHeader className='mb-0'>
          <div className='min-w-0'>
            <Link
              to={hazardDetailBack.to}
              relative={hazardDetailBack.relative}
              className='text-sm text-muted-foreground hover:text-foreground'
            >
              {t('hazardsPage.hazardDetail.back')}
            </Link>
            <ComplianceSubPageTitle className='flex min-w-0 flex-wrap items-center gap-2'>
              <span className='truncate'>
                {getHazardTypeLabel(hazard.type, naLabel, t)} (#{hazard.id})
              </span>
              {needsReview && (
                <Badge variant='warning'>
                  {t('hazardsPage.needsReviewBadge')}
                </Badge>
              )}
            </ComplianceSubPageTitle>
          </div>
          <div className='flex shrink-0 gap-2'>
            <Button
              variant='outline'
              size='sm'
              onClick={handleEditClick}
              disabled={isMutationPending}
            >
              {t('hazardsPage.hazardDetail.edit')}
            </Button>
            {needsReview ? (
              <>
                <Button
                  size='sm'
                  onClick={handleKeepClick}
                  disabled={isMutationPending}
                >
                  {t('hazardsPage.keepUncertain')}
                </Button>
                <Button
                  variant='outline'
                  size='sm'
                  onClick={handleDeleteClick}
                  disabled={isMutationPending}
                >
                  {t('hazardsPage.removeUncertain')}
                </Button>
              </>
            ) : (
              <Button
                variant='outline'
                size='sm'
                onClick={handleDeleteClick}
                disabled={isMutationPending}
              >
                {t('hazardsPage.hazardDetail.delete')}
              </Button>
            )}
          </div>
        </ComplianceSubPageHeader>
      </div>

      <div className='space-y-8 pt-4'>
        <HazardCard
          hazard={hazard}
          machineLocationsById={machineLocationsById}
          onEdit={() => {}}
          onDelete={() => {}}
          isMutationPending={false}
        />
        <HazardRiskAssessmentFlow
          hazardId={hazard.id}
          complianceId={complianceId}
          hazard={hazard}
        />
      </div>

      <AddHazardDialog
        complianceId={complianceId}
        isOpen={isFormOpen}
        onClose={() => setIsFormOpen(false)}
        hazard={hazard}
        onCreate={() => {}}
        onUpdate={handleFormUpdate}
        isCreating={false}
        isUpdating={updateMutation.isPending}
      />

      <DeleteHazardDialog
        isOpen={deletingHazardId !== null}
        onClose={() => setDeletingHazardId(null)}
        onConfirm={handleConfirmDelete}
        isDeleting={deleteMutation.isPending}
        copy={needsReview ? 'remove' : 'delete'}
      />
    </>
  )
}
